/* eslint-disable */
import Cookies from 'js-cookie';

/**
 * Single source of truth for role checks on the frontend.
 *
 * Previously each sidebar inlined a cookie read + JSON parse + includes()
 * chain. Centralising:
 *   - One place to change if role storage migrates (JWT, Vuex, etc.).
 *   - Route guards can import the same check as sidebars.
 *   - Tests can mock one module.
 *
 * Implemented as plain functions rather than a Vue composable because
 * our sidebars are Options API components — they call these as imports,
 * not via inject/setup.
 */

let cachedRoles = null;

/**
 * Reads the userRoles cookie and parses it. Memoised so sidebars that
 * check many roles in one render don't re-parse the JSON each time.
 * Call invalidateRoleCache() after login/logout or cookie mutation.
 */
function loadRoles() {
    if (cachedRoles !== null) return cachedRoles;

    const raw = Cookies.get('userRoles');
    if (!raw) { cachedRoles = []; return cachedRoles; }

    try {
        const parsed = JSON.parse(raw);
        cachedRoles = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        console.warn('userRoles cookie is not valid JSON:', e);
        cachedRoles = [];
    }
    return cachedRoles;
}

// ═════════════════════════════════════════════════════════════════════
// Generic role checks
// ═════════════════════════════════════════════════════════════════════

/**
 * True when the user has the given role.
 * `ROLE_All` is treated as a super-role satisfying any check — matches
 * the legacy convention where admins carry that role.
 */
export function hasRole(role) {
    const roles = loadRoles();
    if (roles.includes('ROLE_All') || roles.includes('ROLE_Super_Admin')) return true;
    return roles.includes(role);
}

/**
 * True when the user has ANY of the listed roles. Convenience for the
 * `v-if="hasRole('A') || hasRole('B')"` pattern.
 */
export function hasAnyRole(...roles) {
    const userRoles = loadRoles();
    if (userRoles.includes('ROLE_All') || userRoles.includes('ROLE_Super_Admin')) return true;
    return roles.some(r => userRoles.includes(r));
}

/** Call after login/logout or any cookie update. */
export function invalidateRoleCache() {
    cachedRoles = null;
}

// ═════════════════════════════════════════════════════════════════════
// Academics-specific helpers
// ═════════════════════════════════════════════════════════════════════
//
// The academics module has three access tiers:
//   1. ROLE_All                           → full access (super-role)
//   2. ROLE_Exam_admin                    → admin: Exams, Lesson Plans,
//                                           Settings submenu, Dashboard,
//                                           plus all teacher views
//   3. ROLE_Access_Exam_Analysis_Module   → teacher: My subjects / My exams,
//                                           Termly conduct only
//
// Users with none of the above cannot enter the module at all — enforced
// by the router guard in academicsGuard.js.
//
// Keep these helpers here rather than inlining `hasAnyRole(...)` calls
// at each use site. If the role set ever changes (e.g. a new
// sub-admin role is introduced), updating the semantic helpers here
// propagates cleanly to sidebar, router, and any page-level guards.

/** True for users who should see anything in the academics module. */
export function canAccessAcademics() {
    return hasAnyRole('ROLE_Exam_admin', 'ROLE_Access_Exam_Analysis_Module');
}

/** True for admin-tier academics access (edit Exams, Settings, etc.). */
export function isAcademicsAdmin() {
    return hasRole('ROLE_Exam_admin');
}

/**
 * True for teacher-tier access. Distinct from isAcademicsAdmin — used
 * when a menu item should show to teachers AND admins, but not to users
 * outside the module. `ROLE_All` and `ROLE_Exam_admin` both qualify
 * because they implicitly grant teacher-tier access.
 */
export function canAccessAcademicsAsTeacher() {
    return canAccessAcademics();
}

// ═════════════════════════════════════════════════════════════════════
// Coding & robotics module helpers
// ═════════════════════════════════════════════════════════════════════
//
// Two access tiers:
//   1. ROLE_All / ROLE_CODING_ADMIN → admin: All schools, Trainers,
//                                     Content, Projects, Exams,
//                                     Assignments — plus all teacher
//                                     views.
//   2. ROLE_CODING_TEACHER          → teacher: My schools, My classes,
//                                     My exams (subset they're assigned
//                                     to via CodingTeacherAssignment).
//
// Admin-only items must hide from plain teachers — enforced both in the
// sidebar (v-if="isCodingAdmin") and the router (`requiresRoles`
// excludes ROLE_CODING_TEACHER for those routes).

/** True for admin-tier coding access (manage schools, trainers, content). */
export function isCodingAdmin() {
    return hasRole('ROLE_CODING_ADMIN');
}

/** True for plain coding teacher (no admin powers). */
export function isCodingTeacher() {
    return hasRole('ROLE_CODING_TEACHER') && !isCodingAdmin();
}

/** True for any coding-module access (admin or teacher). */
export function canAccessCoding() {
    return hasAnyRole('ROLE_CODING_ADMIN', 'ROLE_CODING_TEACHER');
}
